// bibleRef.js — Helper centralizado para parse e roteamento de referências bíblicas.
//
// P0 (Sprint auditoria — iter63): garantir que toda referência exibida no App
// seja clicável e leve à Bíblia correta. Todos os componentes que exibiam
// verse_ref como texto puro agora usam o componente <BibleRefLink> (definido
// abaixo) para renderizar o Link → /biblia/:slug/:chapter.
//
// Suporta formatos comuns:
//   "Sl 147:3 (ARC)"          → /biblia/salmos/147
//   "Sl 147.3"                → /biblia/salmos/147
//   "1 Coríntios 13"          → /biblia/1corintios/13
//   "1Co 13:4-8"              → /biblia/1corintios/13
//   "Ef 4:31-32 (ARC)"        → /biblia/efesios/4
//   "Colossenses 3"           → /biblia/colossenses/3
//   "1Pe 4"                   → /biblia/1pedro/4
//   "Jeremias 17:14"          → /biblia/jeremias/17
//
// Nunca inventa capítulo. Se não conseguir mapear com confiança, retorna null
// (o consumidor renderiza texto simples, sem link quebrado).

import React from "react";
import { Link } from "react-router-dom";
import { BIBLE_BOOKS } from "../data/bibleBooks";

// Índice de busca: mapeia abreviação e nome (com/sem acento, com/sem espaço)
// para o livro canônico.
const _INDEX = (() => {
  const idx = new Map();
  const norm = (s) =>
    (s || "")
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .toLowerCase()
      .replace(/[.\s]/g, "");
  for (const b of BIBLE_BOOKS) {
    idx.set(norm(b.name), b);
    idx.set(norm(b.abbr), b);
    idx.set(norm(b.slug), b);
  }
  // Aliases comuns não canônicos.
  const alias = [
    ["cantico", "cantares"], ["canticos", "cantares"], ["cânticos", "cantares"],
    ["ct", "cantares"], ["cânt", "cantares"],
    ["sl", "salmos"], ["si", "salmos"], // "SI" (I maiúsculo) → Sl
    ["salm", "salmos"], ["salmo", "salmos"], // singular "Salmo 29:11" → Salmos
    ["1co", "1corintios"], ["1cor", "1corintios"], ["icor", "1corintios"],
    ["2co", "2corintios"], ["2cor", "2corintios"],
    ["1ts", "1tessalonicenses"], ["1tess", "1tessalonicenses"],
    ["2ts", "2tessalonicenses"], ["2tess", "2tessalonicenses"],
    ["1tm", "1timoteo"], ["1tim", "1timoteo"],
    ["2tm", "2timoteo"], ["2tim", "2timoteo"],
    ["1pe", "1pedro"], ["1pd", "1pedro"], ["ipe", "1pedro"],
    ["2pe", "2pedro"], ["2pd", "2pedro"],
    ["1jo", "1joao"], ["ijo", "1joao"],
    ["2jo", "2joao"],
    ["3jo", "3joao"],
    ["ap", "apocalipse"], ["apoc", "apocalipse"],
    ["gn", "genesis"], ["ex", "exodo"], ["lv", "levitico"], ["nm", "numeros"], ["dt", "deuteronomio"],
    ["js", "josue"], ["jz", "juizes"], ["rt", "rute"],
    ["1sm", "1samuel"], ["2sm", "2samuel"], ["1rs", "1reis"], ["2rs", "2reis"],
    ["1cr", "1cronicas"], ["2cr", "2cronicas"],
    ["ed", "esdras"], ["ne", "neemias"], ["et", "ester"], ["job", "jo"],
    ["pv", "proverbios"], ["ec", "eclesiastes"], ["cn", "cantares"],
    ["is", "isaias"], ["jr", "jeremias"], ["lm", "lamentacoes"], ["ez", "ezequiel"], ["dn", "daniel"],
    ["os", "oseias"], ["jl", "joel"], ["am", "amos"], ["ob", "obadias"],
    ["jn", "jonas"], ["mq", "miqueias"], ["na", "naum"], ["hc", "habacuque"], ["sf", "sofonias"],
    ["ag", "ageu"], ["zc", "zacarias"], ["ml", "malaquias"],
    ["mt", "mateus"], ["mc", "marcos"], ["lc", "lucas"],
    ["at", "atos"], ["rm", "romanos"], ["gl", "galatas"], ["ef", "efesios"],
    ["fp", "filipenses"], ["cl", "colossenses"],
    ["hb", "hebreus"], ["tg", "tiago"], ["jd", "judas"],
  ];
  for (const [k, slug] of alias) {
    const book = BIBLE_BOOKS.find((b) => b.slug === slug);
    if (book) idx.set(k, book);
  }
  return idx;
})();

/**
 * Parse uma referência bíblica em `{ book, chapter, verseFrom, verseTo }`
 * ou null se não conseguir identificar com segurança.
 *
 * @param {string} ref  ex: "Sl 147:3 (ARC)"
 * @returns {{book: object, chapter: number, verseFrom?: number, verseTo?: number} | null}
 */
export function parseBibleRef(ref) {
  if (!ref || typeof ref !== "string") return null;
  // Remove sufixos comuns (ARC), (NVI), (ACF)…
  let s = ref.replace(/\s*\((?:ARC|NVI|ACF|NAA|ARA|KJV|A21|BLT)\)\s*$/i, "").trim();
  // Regex: (?:capture nome/abrev) espaço (\d+) (:|\.) (\d+) [-,] (\d+)?
  // Aceita: livros com prefixo numérico (1, 2, 3) opcionalmente colados ("1Co", "1 Coríntios").
  const m = s.match(
    /^([1-3]?\s?[A-Za-zÀ-ÿ.]+(?:\s[A-Za-zÀ-ÿ.]+)?)\s+(\d+)(?:[:.](\d+))?(?:\s*-\s*(\d+))?/,
  );
  if (!m) return null;
  const rawBook = m[1].trim();
  const chapter = Number(m[2]);
  const verseFrom = m[3] ? Number(m[3]) : undefined;
  const verseTo = m[4] ? Number(m[4]) : undefined;
  const norm = (v) =>
    (v || "")
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .toLowerCase()
      .replace(/[.\s]/g, "");
  const rawKey = rawBook.toLowerCase().replace(/[.\s]/g, "");
  // Desambiguação ARC: "Jo" = João; "Jó" (com acento) = Jó (Job).
  let book;
  if (rawKey === "jó") book = BIBLE_BOOKS.find((b) => b.slug === "jo");
  else if (rawKey === "jo") book = BIBLE_BOOKS.find((b) => b.slug === "joao");
  if (!book) book = _INDEX.get(norm(rawBook));
  if (!book) return null;
  // Valida capítulo dentro do range do livro.
  if (!Number.isFinite(chapter) || chapter < 1 || chapter > (book.chapters || 999)) {
    return null;
  }
  return { book, chapter, verseFrom, verseTo };
}

/**
 * Converte uma referência em path de rota do app.
 * @returns {string|null}  ex: "/biblia/salmos/147"
 */
export function refToBibliaPath(ref) {
  const parsed = parseBibleRef(ref);
  if (!parsed) return null;
  return `/biblia/${parsed.book.slug}/${parsed.chapter}`;
}

/**
 * <BibleRefLink> — Componente reutilizável.
 * Renderiza `<Link>` clicável se a referência puder ser parseada com segurança;
 * caso contrário, renderiza texto puro (fallback gracioso). Preserva as
 * classes CSS existentes em cada tela via prop `className`.
 *
 * O clique PRIMÁRIO é abrir a Bíblia. Compartilhamento/áudio ficam em botões
 * separados nos consumidores (regra semântica P0).
 */
export function BibleRefLink({
  refText,
  className = "",
  style,
  testid,
  onNavigate,
  title,
  children,
}) {
  const path = refToBibliaPath(refText);
  const label = children != null ? children : refText;
  if (!path) {
    return (
      <span className={className} style={style} data-testid={testid}>
        {label}
      </span>
    );
  }
  return (
    <Link
      to={path}
      className={className}
      style={style}
      data-testid={testid}
      onClick={onNavigate}
      title={title || `Abrir ${refText} na Bíblia`}
    >
      {label}
    </Link>
  );
}
